/**
 * HomePage Component
 * 
 * Main application page that provides the complete race strategy interface
 * Includes track selection, car configuration, driver data input, and strategy results
 */

import React, { useMemo, useState, useEffect } from "react";
import { StrategyResult } from "../components/StrategyResult";
// import { StrategyChart } from "../components/StrategyChart";
import { TimePicker } from "../components/TimePicker";
import { FuelInput } from "../components/FuelInput";
import { NumberInput } from "../components/NumberInput";
import { AdBanner } from "../components/AdBanner";
import { ConsentBanner } from "../components/ConsentBanner";
import FuelSavingSuggestions from "../components/FuelSavingSuggestions";
import { StaticPlannerPanel } from "../components/StaticPlannerPanel";
// import { Garage61Login } from "../components/Garage61Login";
// import { IRacingLogin } from "../components/IRacingLogin";
import { useRaceStrategy } from "../hooks/useRaceStrategy";
// import { Strategy } from "../types/strategy";
// import { IRacingCredentials } from "../types/strategy";
// import { Garage61Credentials } from "../types/strategy";

import type { RacePlan, StaticStrategyInput } from "../utils/staticStrategyPlanner";
import type { FuelSavingCalibration } from "../types/strategyCalibration";

interface HomePageProps {
  onSelectPlan?: (plan: RacePlan, input: StaticStrategyInput) => void;
  selectedPlanId?: string | null;
}

/**
 * HomePage component - Main application interface
 * @returns JSX element containing the complete race strategy application
 */
export const HomePage: React.FC<HomePageProps> = ({ onSelectPlan, selectedPlanId }) => {
  // Persistent state helpers
  function getPersistedNumber(key: string, fallback: number) {
    if (typeof window === 'undefined') return fallback;
    const v = localStorage.getItem(key);
    return v !== null && !isNaN(Number(v)) ? Number(v) : fallback;
  }
  function setPersistedNumber(key: string, value: number) {
    if (typeof window !== 'undefined') localStorage.setItem(key, String(value));
  }

  const [fuelSaveLapTime, setFuelSaveLapTime] = useState<number>(() => getPersistedNumber('fuelSaveLapTime', 0));
  const [fuelSaveFuelConsumption, setFuelSaveFuelConsumption] = useState<number>(() => getPersistedNumber('fuelSaveFuelConsumption', 0));

  // Get all strategy data and functions from the custom hook
  const {
    // State values
    // selectedTrackId,
    // selectedCarClassId,
    raceDuration,
    driverData,
    // garage61Data,
    // isLoadingGarage61,
    // garage61Sessions,
    // isLoadingSessions,
    userActualLapTime,
    userActualFuelConsumption,
    // iracingTracks,
    // iracingCarClasses,
    // isLoadingIRacing,
    
    // Data objects - simplified configuration
    // selectedTrack,
    // selectedCarClass,
    // tracks,
    // carClasses,
    // categories,
    pitStopDetails,
    
    // Calculated strategies
    normalStrategy,
    fuelSaveStrategy,
    
    // Action functions
    // setSelectedTrackId,
    // setSelectedCarClassId,
    setRaceDuration,
    updateDriverData,
    setUserActualLapTime,
    setUserActualFuelConsumption,
    // authenticateIRacing,
    // logoutFromIRacing,
    // fetchGarage61Data,
    // logoutFromGarage61,
    // selectGarage61Session,
    
    // Garage61 service state
    // isGarage61Authenticated
    // isIRacingAuthenticated
  } = useRaceStrategy();

  // Persist all values to localStorage on change
  useEffect(() => { setPersistedNumber('fuelSaveLapTime', fuelSaveLapTime); }, [fuelSaveLapTime]);
  useEffect(() => { setPersistedNumber('fuelSaveFuelConsumption', fuelSaveFuelConsumption); }, [fuelSaveFuelConsumption]);
  useEffect(() => { setPersistedNumber('raceDuration', raceDuration); }, [raceDuration]);
  useEffect(() => { setPersistedNumber('fuelCapacity', driverData.fuelCapacity); }, [driverData.fuelCapacity]);
  useEffect(() => { setPersistedNumber('fuelConsumption', driverData.fuelConsumption); }, [driverData.fuelConsumption]);
  useEffect(() => { setPersistedNumber('avgLapTime', driverData.avgLapTime); }, [driverData.avgLapTime]);
  useEffect(() => { setPersistedNumber('pitInTime', driverData.pitStopDetails.pitInTime); }, [driverData.pitStopDetails.pitInTime]);
  useEffect(() => { setPersistedNumber('pitOutTime', driverData.pitStopDetails.pitOutTime); }, [driverData.pitStopDetails.pitOutTime]);
  useEffect(() => { setPersistedNumber('fuelingTime', driverData.pitStopDetails.fuelingTime); }, [driverData.pitStopDetails.fuelingTime]);

  const manualCalibration: FuelSavingCalibration | undefined = useMemo(() => {
    if (
      driverData.avgLapTime <= 0 ||
      driverData.fuelConsumption <= 0 ||
      fuelSaveLapTime <= 0 ||
      fuelSaveFuelConsumption <= 0
    ) {
      return undefined;
    }

    return {
      baselineLapTime: driverData.avgLapTime,
      baselineFuelPerLap: driverData.fuelConsumption,
      saveLapTime: fuelSaveLapTime,
      saveFuelPerLap: fuelSaveFuelConsumption,
    };
  }, [driverData.avgLapTime, driverData.fuelConsumption, fuelSaveLapTime, fuelSaveFuelConsumption]);

  const comparisonSummary = useMemo(() => {
    if (!manualCalibration) {
      return null;
    }

    const lapTimeDelta = manualCalibration.saveLapTime - manualCalibration.baselineLapTime;
    const fuelDelta = manualCalibration.baselineFuelPerLap - manualCalibration.saveFuelPerLap;
    const fuelSavePercent = manualCalibration.baselineFuelPerLap > 0
      ? (fuelDelta / manualCalibration.baselineFuelPerLap) * 100
      : 0;

    if (fuelDelta <= 0 || lapTimeDelta <= 0 || fuelSavePercent <= 0) {
      return null;
    }

    return {
      lapTimeDelta,
      fuelDelta,
      fuelSavePercent,
      timeLossPerPercent: lapTimeDelta / fuelSavePercent,
    };
  }, [manualCalibration]);

  // Track and car class selection removed - using simplified configuration

  // const categoryOptions = categories.map(category => ({
  //   value: category,
  //   label: category
  // }));

  // Track and car class filtering removed - using simplified configuration

  // Create a login function that matches the expected interface - COMMENTED OUT
  /*
  const handleGarage61Login = async (credentials: Garage61Credentials): Promise<boolean> => {
    try {
      await fetchGarage61Data();
      return true;
    } catch (error) {
      console.error("Login failed:", error);
      return false;
    }
  };
  */

  // Create iRacing login handler
  // const handleIRacingLogin = async (credentials: IRacingCredentials): Promise<boolean> => {
  //   try {
  //     return await authenticateIRacing(credentials);
  //   } catch (error) {
  //     console.error("iRacing login failed:", error);
  //     return false;
  //   }
  // };

  // Show login screen if not authenticated with iRacing
  // if (!isIRacingAuthenticated) {
  //   return (
  //     <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center">
  //       <div className="max-w-md w-full mx-4">
  //         <div className="text-center mb-8">
  //           <h1 className="text-3xl font-bold text-white mb-2">Smart Race Engineer</h1>
  //           <p className="text-slate-300">iRacing Race Strategy Calculator</p>
  //         </div>
  //         
  //         <IRacingLogin
  //           onLogin={handleIRacingLogin}
  //           onLogout={logoutFromIRacing}
  //           isAuthenticated={isIRacingAuthenticated}
  //           isLoading={isLoadingIRacing}
  //         />
  //         
  //         <div className="mt-6 text-center text-sm text-slate-400">
  //           <p>Connect to iRacing to access real-time track and car data</p>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto py-8 px-4">
        {/* Application header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">
            Smart Race Strategy
          </h1>
          <p className="text-slate-400 text-lg">
            Professional Strategy Calculator for Endurance Racing
          </p>
        </div>

        {/* Top Banner Ad: show only when user configured core inputs (meaningful content) */}
        {driverData.fuelConsumption > 0 && driverData.fuelCapacity > 0 && driverData.avgLapTime > 0 && (
          <div className="mb-6">
            <AdBanner 
              adSlot="1234567890" 
              format="auto" 
              className="text-center"
            />
          </div>
        )}

        {/* Main content grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Input Configuration */}
          <div className="lg:col-span-1">
            {/* Race Configuration Section */}
            <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 mb-6">
              <h2 className="text-xl font-semibold mb-4 text-white">Race Configuration</h2>
              <div className="mb-4 rounded-lg border border-slate-700 bg-slate-900/40 p-3 text-xs text-slate-400">
                These values are your full-speed baseline. Enter the pace and fuel burn you can sustain when driving flat out.
              </div>
              
              {/* Race duration */}
              <div className="mb-4">
                <NumberInput
                  label="Race Length (minutes)"
                  value={raceDuration}
                  onChange={setRaceDuration}
                  placeholder="360"
                  min={20}
                  max={1440}
                />
              </div>

              {/* Fuel capacity */}
              <div className="mb-4">
                <NumberInput
                  label="Fuel Capacity (L)"
                  value={driverData.fuelCapacity}
                  onChange={(value) => updateDriverData({ fuelCapacity: value })}
                  placeholder="120"
                  min={50}
                  max={200}
                />
              </div>

              {/* Fuel consumption */}
              <div className="mb-4">
                <FuelInput
                  label="Full-Speed Fuel Consumption (L/lap)"
                  value={driverData.fuelConsumption}
                  onChange={(value) => updateDriverData({ fuelConsumption: value })}
                  placeholder="3.20"
                  min={1}
                  max={10}
                />
              </div>

              {/* Average lap time */}
              <div className="mb-4">
                <TimePicker
                  label="Full-Speed Average Lap Time"
                  value={driverData.avgLapTime}
                  onChange={(value) => updateDriverData({ avgLapTime: value })}
                  placeholder="01:47.500"
                />
                <div className="text-xs text-slate-500 mt-1">
                  Pure driving time (excluding pit stops)
                </div>
              </div>

              {/* iRacing Integration */}
              {/* <div className="mb-6">
                <IRacingLogin
                  onLogin={handleIRacingLogin}
                  onLogout={logoutFromIRacing}
                  isAuthenticated={isIRacingAuthenticated}
                  isLoading={isLoadingIRacing}
                />
              </div> */}
            </div>

            <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 mb-6">
              <h2 className="text-xl font-semibold mb-4 text-white">Fuel-Save Reference</h2>
              <div className="mb-4 rounded-lg border border-slate-700 bg-slate-900/40 p-3 text-xs text-slate-400">
                Enter one known fuel-saving reference run. The planner uses it to measure how much lap time you lose for each percent of fuel saved.
              </div>

              <div className="grid grid-cols-1 gap-4">
                <TimePicker
                  label="Fuel-Save Lap Time"
                  value={fuelSaveLapTime}
                  onChange={setFuelSaveLapTime}
                  placeholder="01:49.000"
                />

                <FuelInput
                  label="Fuel-Save Fuel Consumption (L/lap)"
                  value={fuelSaveFuelConsumption}
                  onChange={setFuelSaveFuelConsumption}
                  placeholder="3.05"
                  min={1}
                  max={10}
                />
              </div>

              <div className="text-xs text-slate-500 mt-3">
                Keep the main race configuration as the fastest baseline. This section should only describe the slower, lower-consumption reference.
              </div>
            </div>

            {/* Pit Stop Configuration Section */}
            <div className="bg-slate-800 p-6 rounded-xl border border-slate-700">
              <h2 className="text-xl font-semibold mb-4 text-white">Pit Stop Details</h2>
              
              {/* Pit stop timing inputs */}
              <div className="grid grid-cols-1 gap-4 mb-4">
                <NumberInput
                  label="Pit In Time"
                  value={driverData.pitStopDetails.pitInTime}
                  onChange={(value) => updateDriverData({ 
                    pitStopDetails: { 
                      ...driverData.pitStopDetails, 
                      pitInTime: value 
                    } 
                  })}
                  placeholder="15"
                  min={0}
                  max={60}
                />
                
                <NumberInput
                  label="Fueling Time (s)"
                  value={driverData.pitStopDetails.fuelingTime}
                  onChange={(value) => updateDriverData({ 
                    pitStopDetails: { 
                      ...driverData.pitStopDetails, 
                      fuelingTime: value 
                    } 
                  })}
                  placeholder="50"
                  min={10}
                  max={120}
                />
                <div className="text-xs text-slate-500 mt-1">
                  Time to refuel to maximum capacity
                </div>
                
                <NumberInput
                  label="Pit Out Time"
                  value={driverData.pitStopDetails.pitOutTime}
                  onChange={(value) => updateDriverData({ 
                    pitStopDetails: { 
                      ...driverData.pitStopDetails, 
                      pitOutTime: value 
                    } 
                  })}
                  placeholder="10"
                  min={0}
                  max={60}
                />
              </div>

              {/* Calculated pit stop details display */}
              <div className="p-3 bg-slate-700 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-lg font-bold text-yellow-400">
                      {pitStopDetails.totalTime.toFixed(1)}s
                    </div>
                    <div className="text-xs text-slate-400">Total Pit Time</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-blue-400">
                      {pitStopDetails.refuelTime.toFixed(2)}s/L
                    </div>
                    <div className="text-xs text-slate-400">Refuel Rate</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Strategy Results */}
          <div className="lg:col-span-2">
            {/* Sidebar Ad removed to reduce ad density */}

            {normalStrategy ? (
              <div className="space-y-6">
                {/* Normal Strategy Display */}
                <StrategyResult 
                  strategy={normalStrategy} 
                  title="Normal Strategy" 
                  userActualLapTime={userActualLapTime}
                  onUserActualLapTimeChange={setUserActualLapTime}
                  fuelCapacity={driverData.fuelCapacity}
                  pitStopDetails={driverData.pitStopDetails}
                />

                {/* Fuel Saving Suggestions */}
                <FuelSavingSuggestions 
                  stints={normalStrategy.stintBreakdown} 
                  fuelCapacity={driverData.fuelCapacity}
                />

                {/* Fuel Save Strategy Display (if applicable) */}
                {(() => {
                  console.log("Fuel Save Strategy Render Check:", {
                    fuelSaveStrategy,
                    hasFuelSaveStrategy: !!fuelSaveStrategy,
                    fuelSaveStrategyType: typeof fuelSaveStrategy,
                    fuelSaveStrategyKeys: fuelSaveStrategy ? Object.keys(fuelSaveStrategy) : null
                  });
                  return fuelSaveStrategy && (
                    <StrategyResult 
                      strategy={fuelSaveStrategy} 
                      title="Fuel Save Strategy" 
                      userActualLapTime={userActualLapTime}
                      onUserActualLapTimeChange={setUserActualLapTime}
                      userActualFuelConsumption={userActualFuelConsumption}
                      onUserActualFuelConsumptionChange={setUserActualFuelConsumption}
                      fuelCapacity={driverData.fuelCapacity}
                      pitStopDetails={driverData.pitStopDetails}
                    />
                  );
                })()}

                {/* Simple Fuel Save Strategy (+1 Lap per Stint) - TODO: re-enable when simpleFuelSave is added to Strategy type */}

                {/* Strategy Comparison Chart - REMOVED */}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-slate-400 text-lg">
                  Configure your race settings to see strategy calculations
                </div>
              </div>
            )}

            {/* Static Strategy Planner — compares all race plans */}
            {driverData.fuelConsumption > 0 && driverData.fuelCapacity > 0 && driverData.avgLapTime > 0 && (
              <div className="mt-6">
                {comparisonSummary && (
                  <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 mb-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h2 className="text-xl font-semibold text-white">Comparison Summary</h2>
                        <p className="text-sm text-slate-400 mt-1">
                          Validates your manual full-speed and fuel-save references before feeding them into the planner.
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-slate-700/50 rounded-lg">
                        <div className="text-slate-400 text-sm">Avg Time Loss</div>
                        <div className="text-2xl font-bold text-white">
                          +{comparisonSummary.lapTimeDelta.toFixed(3)}s
                        </div>
                        <div className="text-slate-400 text-xs mt-1">
                          Fuel-save reference vs full speed
                        </div>
                      </div>
                      <div className="p-4 bg-slate-700/50 rounded-lg">
                        <div className="text-slate-400 text-sm">Fuel Saved / Lap</div>
                        <div className="text-2xl font-bold text-white">{comparisonSummary.fuelDelta.toFixed(3)}L</div>
                        <div className="text-slate-400 text-xs mt-1">
                          {driverData.fuelConsumption.toFixed(2)}L to {fuelSaveFuelConsumption.toFixed(2)}L
                        </div>
                      </div>
                      <div className="p-4 bg-slate-700/50 rounded-lg">
                        <div className="text-slate-400 text-sm">Observed Save</div>
                        <div className="text-2xl font-bold text-blue-400">{comparisonSummary.fuelSavePercent.toFixed(2)}%</div>
                        <div className="text-slate-400 text-xs mt-1">
                          Relative to the full-speed baseline
                        </div>
                      </div>
                      <div className="p-4 bg-slate-700/50 rounded-lg">
                        <div className="text-slate-400 text-sm">Time Loss per 1% Save</div>
                        <div className="text-2xl font-bold text-white">
                          {comparisonSummary.timeLossPerPercent.toFixed(3)}s
                        </div>
                        <div className="text-slate-400 text-xs mt-1">
                          Used for planner calibration
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <StaticPlannerPanel
                  raceDurationMinutes={raceDuration}
                  fuelCapacity={driverData.fuelCapacity}
                  avgFuelPerLap={driverData.fuelConsumption}
                  avgLapTime={driverData.avgLapTime}
                  pitInTime={driverData.pitStopDetails.pitInTime}
                  pitOutTime={driverData.pitStopDetails.pitOutTime}
                  fuelingTime={driverData.pitStopDetails.fuelingTime}
                  prefillFuelSavingCalibration={manualCalibration}
                  onSelectPlan={onSelectPlan}
                  selectedPlanId={selectedPlanId}
                />
              </div>
            )}
          </div>
        </div>

        {/* Bottom Banner Ad: show only when strategy is available (meaningful content) */}
        {normalStrategy && (
          <div className="mt-8">
            <AdBanner 
              adSlot="1122334455" 
              format="auto" 
              className="text-center"
            />
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 pt-8 border-t border-slate-700 text-center">
          <div className="text-slate-400 text-sm space-x-4">
            <a href="/privacy-policy.html" className="hover:text-white transition-colors">Privacy Policy</a>
            <a href="/terms.html" className="hover:text-white transition-colors">Terms</a>
            <a href="/about.html" className="hover:text-white transition-colors">About</a>
            <a href="/contact.html" className="hover:text-white transition-colors">Contact</a>
            <a href="/guide.html" className="hover:text-white transition-colors">Guide</a>
            <a href="/faq.html" className="hover:text-white transition-colors">FAQ</a>
            <a href="/blog/index.html" className="hover:text-white transition-colors">Blog</a>
            <a href="/impressum.html" className="hover:text-white transition-colors">Impressum</a>
            <a href="/datenschutz.html" className="hover:text-white transition-colors">Datenschutz</a>
            <span className="inline-block">© 2025 Smart Race Engineer</span>
          </div>
        </div>
      </div>
      
      {/* GDPR Consent Banner */}
      <ConsentBanner />
    </div>
  );
};
